import React, { useMemo } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Chip, Grid, Link, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import { CompetitorFiguresGrid, CompetitorRefreshBar, useCompetitorMetrics } from "./CompetitorFigures";
import { mergeMarketLeaderRows, mergeScorecard } from "./competitorMetrics";
import RoadmapBoard from "./RoadmapTodo";
import {
  CHANNEL_PLAYBOOKS, CHANNEL_SCORECARD, CLOSEST_COMPETITORS, COMMON_THREAD, COMPETITOR_MARKETING, DATA_CHECKED,
  FEATURE_COLUMNS, FEATURE_ROWS, FUNNEL_COLUMNS, FUNNEL_ROWS, KPIS, KPI_NOTE, MARKET_LEADER_COLUMNS,
  MARKET_LEADER_ROWS, PARENT_COMMUNITIES, POSITIONING_LINE, POSITIONING_NOTES, PRODUCT_SUMMARY, ROADMAP_NOTE,
  ROADMAP_PHASES, SCORECARD_NOTE, SOURCES, SUMMARY_CARDS, VERDICT,
} from "./marketingCompetitorsData";

const PURPLE = "#7c3aed";

const TONES = {
  purple: { bg: "#f5f3ff", fg: PURPLE },
  blue: { bg: "#eff6ff", fg: "#2563eb" },
  green: { bg: "#ecfdf5", fg: "#059669" },
  orange: { bg: "#fff7ed", fg: "#ea580c" },
  teal: { bg: "#f0fdfa", fg: "#0d9488" },
  red: { bg: "#fef2f2", fg: "#dc2626" },
};

const PRIORITY_COLOR = { P1: "error", P2: "warning", P3: "success" };

const HEAD_CELL_SX = { fontWeight: 700, bgcolor: "#fafafa", whiteSpace: "nowrap", fontSize: 13 };
const CELL_SX = { fontSize: 13, verticalAlign: "top" };

const URL_RE = /(https?:\/\/[^\s)]+)/g;

// Turns bare URLs inside research notes into links.
export function LinkifiedText({ text }) {
  const parts = String(text).split(URL_RE);
  return (
    <>
      {parts.map((part, i) =>
        /^https?:\/\//.test(part) ? (
          <Link key={i} href={part} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: "break-all" }}>
            {part}
          </Link>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
}

function SectionCard({ id, title, subtitle, children }) {
  return (
    <Paper sx={{ ...CARD_SX, mb: 2 }} component="section" aria-labelledby={`${id}-title`} data-testid={`competitors-${id}`}>
      <Typography id={`${id}-title`} variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>{subtitle}</Typography>
      )}
      <Box sx={{ mt: subtitle ? 0.5 : 1 }}>{children}</Box>
    </Paper>
  );
}

function SimpleTable({ label, columns, rows, firstColBold = true, minWidth = 720 }) {
  return (
    <TableContainer sx={{ border: "1px solid #eef0f4", borderRadius: 2 }}>
      <Table size="small" aria-label={label} sx={{ minWidth }}>
        <TableHead>
          <TableRow>
            {columns.map((c) => <TableCell key={c || "aspect"} sx={HEAD_CELL_SX}>{c}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row[0]} hover>
              {row.map((cell, i) => (
                <TableCell key={i} sx={{ ...CELL_SX, fontWeight: i === 0 && firstColBold ? 600 : 400 }}>{cell}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function PriorityChip({ value }) {
  return <Chip size="small" label={value} color={PRIORITY_COLOR[value] || "default"} sx={{ fontWeight: 700 }} />;
}

export default function MarketingCompetitors() {
  const metrics = useCompetitorMetrics();
  // Numbers come from the live/manual figures; the strategy text stays as researched.
  const scorecard = useMemo(() => mergeScorecard(CHANNEL_SCORECARD, metrics.index), [metrics.index]);
  const marketLeaderRows = useMemo(() => mergeMarketLeaderRows(MARKET_LEADER_ROWS, metrics.index), [metrics.index]);

  return (
    <AnalyticsPageShell
      title="Competitors"
      subtitle="Who our closest competitors are, who leads the UK 11+ market, how they market, and what 11 Plus Magic should do to reach that level."
    >
      <CompetitorRefreshBar metrics={metrics} />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
        <Chip icon={<EventAvailableIcon />} label={DATA_CHECKED} variant="outlined" sx={{ fontWeight: 600, color: PURPLE, borderColor: PURPLE, "& .MuiChip-icon": { color: PURPLE } }} data-testid="competitors-checked" />
        <Typography variant="caption" color="text.secondary">
          Public counts and the brands’ own claims as read on that date. “Not found” means it couldn’t be read.
        </Typography>
      </Stack>

      {/* 1. Summary cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {SUMMARY_CARDS.map((card) => {
          const tone = TONES[card.tone] || TONES.purple;
          return (
            <Grid item xs={12} sm={6} lg={4} key={card.key}>
              <Paper sx={{ ...CARD_SX, height: "100%", borderLeft: `4px solid ${tone.fg}` }}>
                <Typography variant="caption" sx={{ color: tone.fg, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
                  {card.label}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{card.name}</Typography>
                <Typography variant="body2" color="text.secondary">{card.detail}</Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Paper sx={{ ...CARD_SX, mb: 2, bgcolor: "#faf5ff", borderColor: "#e9d5ff" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PURPLE, mb: 0.5 }}>The short version</Typography>
        <Typography variant="body2">{VERDICT}</Typography>
      </Paper>

      {/* 2. Closest competitors */}
      <SectionCard id="closest" title="Our closest competitors" subtitle="Picked by how closely their product matches what 11 Plus Magic does.">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{PRODUCT_SUMMARY}</Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {CLOSEST_COMPETITORS.map((c) => (
            <Grid item xs={12} md={6} key={c.title}>
              <Box sx={{ p: 1.5, border: "1px solid #eef0f4", borderRadius: 2, height: "100%" }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{c.title}</Typography>
                <Typography variant="body2" color="text.secondary">{c.detail}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Feature comparison</Typography>
        <SimpleTable label="Feature comparison" columns={FEATURE_COLUMNS} rows={FEATURE_ROWS} minWidth={960} />
      </SectionCard>

      {/* 3. Market leaders per channel */}
      <SectionCard id="scorecard" title="Market leaders per channel" subtitle="Best UK 11+ competitor on each marketing channel, ordered by priority for a small team.">
        <TableContainer sx={{ border: "1px solid #eef0f4", borderRadius: 2 }}>
          <Table size="small" aria-label="Channel scorecard" sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow>
                {["Channel", "Best competitor", "Their number", "11 Plus Magic", "Gap", "Priority"].map((c) => (
                  <TableCell key={c} sx={HEAD_CELL_SX}>{c}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {scorecard.map((r) => (
                <TableRow key={r.channel} hover>
                  <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{r.channel}</TableCell>
                  <TableCell sx={CELL_SX}>{r.leader}</TableCell>
                  <TableCell sx={CELL_SX}>{r.theirNumber}</TableCell>
                  <TableCell sx={CELL_SX}>{r.ours}</TableCell>
                  <TableCell sx={CELL_SX}>{r.gap}</TableCell>
                  <TableCell sx={CELL_SX}><PriorityChip value={r.priority} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>{SCORECARD_NOTE}</Typography>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2.5, mb: 1 }}>Who is top in the UK 11+ market: followers by platform</Typography>
        <SimpleTable label="Followers by platform" columns={MARKET_LEADER_COLUMNS} rows={marketLeaderRows} minWidth={960} />

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2.5, mb: 1 }}>Where parents talk</Typography>
        <SimpleTable
          label="Parent communities"
          columns={["Community", "Size", "Why it matters"]}
          rows={PARENT_COMMUNITIES.map((c) => [c.name, c.size, c.why])}
          minWidth={600}
        />
      </SectionCard>

      {/* Live and manual figures behind the tables above */}
      <SectionCard
        id="figures"
        title="Figures by brand"
        subtitle="Live: refreshed from the App Store and YouTube. Manual: typed in by an admin (pencil to update). The tables above use these numbers."
      >
        <CompetitorFiguresGrid metrics={metrics} />
      </SectionCard>

      {/* 4. What they do for marketing */}
      <SectionCard id="marketing" title="What they do for marketing" subtitle="Claims in quotes are the brands’ own words from their sites.">
        {COMPETITOR_MARKETING.map((c, idx) => (
          <Accordion key={c.name} defaultExpanded={idx === 0} disableGutters elevation={0} sx={{ border: "1px solid #eef0f4", borderRadius: 2, mb: 1, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                <Typography sx={{ fontWeight: 700 }}>{c.name}</Typography>
                <Chip size="small" label={c.role} sx={{ bgcolor: "#f5f3ff", color: PURPLE, fontWeight: 600 }} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {c.why && <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{c.why}</Typography>}
              <Grid container spacing={1.5}>
                {c.aspects.map((a) => (
                  <Grid item xs={12} md={6} key={a.label}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: PURPLE, textTransform: "uppercase" }}>{a.label}</Typography>
                    <Typography variant="body2">{a.value}</Typography>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2.5, mb: 0.5 }}>Channel by channel</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{COMMON_THREAD}</Typography>
        {CHANNEL_PLAYBOOKS.map((ch) => (
          <Accordion key={ch.id} disableGutters elevation={0} sx={{ border: "1px solid #eef0f4", borderRadius: 2, mb: 1, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                <Typography sx={{ fontWeight: 700 }}>{ch.name}</Typography>
                <Chip size="small" variant="outlined" label={ch.priority.split(",")[0]} color={PRIORITY_COLOR[ch.priority.slice(0, 2)] || "default"} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>{ch.lead}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: PURPLE, textTransform: "uppercase" }}>Evidence</Typography>
              <Box component="ul" sx={{ mt: 0.5, mb: 1.5, pl: 2.5 }}>
                {ch.evidence.map((e) => (
                  <Typography component="li" variant="body2" key={e} sx={{ mb: 0.5 }}><LinkifiedText text={e} /></Typography>
                ))}
              </Box>
              <SimpleTable label={`${ch.name} gap`} columns={["Metric", "Leader", "11 Plus Magic"]} rows={ch.gap.map((g) => [g.metric, g.leader, g.ours])} minWidth={520} />
              <Typography variant="caption" sx={{ display: "block", fontWeight: 700, color: PURPLE, textTransform: "uppercase", mt: 1.5 }}>To reach that level</Typography>
              <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 2.5 }}>
                {ch.todo.map((t) => <Typography component="li" variant="body2" key={t} sx={{ mb: 0.5 }}>{t}</Typography>)}
              </Box>
              <Typography variant="body2"><strong>90-day target:</strong> {ch.target}</Typography>
              <Typography variant="body2" color="text.secondary"><strong>Effort:</strong> {ch.effort} · <strong>Priority:</strong> {ch.priority}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </SectionCard>

      {/* 5. Roadmap */}
      <SectionCard id="roadmap" title="Our roadmap to reach that level" subtitle={ROADMAP_NOTE}>
        <Box sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: "#f5f3ff", border: "1px solid #e9d5ff" }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: PURPLE, textTransform: "uppercase" }}>Positioning line</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }} data-testid="competitors-positioning">{POSITIONING_LINE}</Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
            {POSITIONING_NOTES.map((n) => <Typography component="li" variant="body2" key={n}>{n}</Typography>)}
          </Box>
        </Box>

        <RoadmapBoard phases={ROADMAP_PHASES} />

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Funnel to build</Typography>
        <SimpleTable label="Funnel comparison" columns={FUNNEL_COLUMNS} rows={FUNNEL_ROWS} minWidth={1100} />

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2.5, mb: 1 }}>KPIs to track on the Overview tab</Typography>
        <SimpleTable
          label="Marketing KPIs"
          columns={["KPI", "Definition", "Break down by", "Why it matters"]}
          rows={KPIS.map((k) => [k.kpi, k.definition, k.breakdown, k.why])}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{KPI_NOTE}</Typography>
      </SectionCard>

      {/* 6. Sources */}
      <SectionCard id="sources" title="Sources" subtitle={`${DATA_CHECKED}. Links open in a new tab.`}>
        <Grid container spacing={1}>
          {SOURCES.map((s) => (
            <Grid item xs={12} sm={6} lg={4} key={s.href}>
              <Link href={s.href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: 13, fontWeight: 600 }}>
                {s.title}
              </Link>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{s.site}</Typography>
            </Grid>
          ))}
        </Grid>
      </SectionCard>
    </AnalyticsPageShell>
  );
}
